import React, { useMemo } from 'react'
import { Button, ListItemText, Menu, MenuItem } from '@mui/material'
import { isEmpty } from 'lodash'
import ChevronUpIcon from '../../../icons/ChevronUp'
import { useCurrentPractice } from '../../../contexts/PracticeContext'
import { usePopover } from '../../../hooks/usePopover'
import { isIndependentPractice } from '../../../util/practice'
import { FEATURE_UPDATES_URL, PIMS_USER_GUIDES_URL, SIDEBAR_PILL_SX } from './constants'

/**
 * Replaces the sidebar's FAQs pill with a "Resources" menu that opens upward (DASH-9523).
 *
 * "What's new" points at marketing's feature-updates page and is shown only to practices without a
 * consolidator — enterprise clinics get their release comms elsewhere (DASH-9522).
 */
const ResourcesMenu: React.FC<{ faqUrl: string }> = ({ faqUrl }) => {
	const { currentPracticeObject } = useCurrentPractice()
	const { anchorRef, open, handleOpen, handleClose } = usePopover()

	const isSMBPractice = useMemo(
		() => !isEmpty(currentPracticeObject) && isIndependentPractice(currentPracticeObject),
		[currentPracticeObject]
	)

	const links = useMemo(
		() => [
			{ label: 'FAQs', url: faqUrl },
			{ label: 'PIMS user guides', url: PIMS_USER_GUIDES_URL },
			...(isSMBPractice ? [{ label: "What's new", url: FEATURE_UPDATES_URL }] : [])
		],
		[faqUrl, isSMBPractice]
	)

	return (
		<>
			<Button
				fullWidth
				color="primary"
				size="small"
				sx={SIDEBAR_PILL_SX}
				ref={anchorRef}
				onClick={handleOpen}
				endIcon={<ChevronUpIcon fontSize="small" />}
				data-cy="resources-button"
			>
				Resources
			</Button>
			<Menu
				anchorEl={anchorRef.current}
				anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
				transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
				keepMounted
				onClose={handleClose}
				open={open}
				slotProps={{ paper: { sx: { width: 220 } } }}
			>
				{links.map(({ label, url }) => (
					<MenuItem
						key={label}
						component="a"
						href={url}
						target="_blank"
						rel="noreferrer"
						onClick={handleClose}
					>
						<ListItemText primary={label} slotProps={{ primary: { variant: 'subtitle2' } }} />
					</MenuItem>
				))}
			</Menu>
		</>
	)
}

export default ResourcesMenu
